import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { INDUSTRIES, getIndustryBySlug } from "@/data/industries";
import { SERVICES } from "@/data/services";
import { PORTFOLIO } from "@/data/portfolio";
import { NEEDS } from "@/data/needs";
import { SITE } from "@/data/config";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import ServiceCard from "@/components/ServiceCard";
import RelatedContent from "@/components/RelatedContent";
import CTABlock from "@/components/CTABlock";
import PortfolioCover from "@/components/PortfolioCover";
import { learnMoreForIndustry, CTA_CONTEXT } from "@/data/educational";
import LearnMoreCard from "@/components/LearnMoreCard";
import { billingLabel } from "@/lib/billing";
import { resolvePricing } from "@/lib/pricing";

// Map industry slug -> industry-combo growth page. Surfaced as a secondary
// link on each Industries index card so link equity flows to the newer pages.
const INDUSTRY_COMBO_LINKS = {
  "real-estate": { to: "/branding-for-real-estate-agents-canada", label: "Branding for real estate agents" },
  "home-services": { to: "/google-business-profile-for-home-services", label: "GBP for home services" },
  "restaurants-and-bars": { to: "/website-design-for-restaurants-canada", label: "Websites for restaurants" },
  "legal-services": { to: "/website-design-for-law-firms-canada", label: "Websites for law firms" },
  "financial-services": { to: "/branding-for-financial-advisors-canada", label: "Branding for financial advisors" },
  "health-and-wellness": { to: "/branding-for-dentists-canada", label: "Branding for dental practices" },
  "construction": { to: "/website-design-for-contractors-canada", label: "Websites for contractors" },
};

// ---------------------------------------------------------------------------
// Industries hub
// ---------------------------------------------------------------------------
export const IndustriesIndex = () => {
  useSeo(SEO.industries);
  // Real Estate is featured at the top.
  const realEstate = INDUSTRIES.find((i) => i.slug === "real-estate");
  const rest = INDUSTRIES.filter((i) => i.slug !== "real-estate");

  return (
    <main data-testid="industries-page" className="bg-black">
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Industries" }]} />
          <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
            Industries we know how to work in.
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-lg">
            Pick the world you live in. Each page is written for your decision-making environment, not a generic agency template.
          </p>
        </div>
      </section>

      {/* Real Estate spotlight card */}
      {realEstate ? (
        <section className="bg-[#FFC600] text-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Overline className="mb-4">Featured industry</Overline>
              <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.92]">
                {realEstate.heroHeadline}
              </h2>
              <p className="mt-5 max-w-2xl text-black/80 text-base lg:text-lg">{realEstate.heroSub}</p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to={`/industries/${realEstate.slug}`}
                data-testid="industries-real-estate-cta"
                className="inline-flex items-center justify-between bg-black text-[#FFC600] font-semibold px-6 py-4 border border-white/15 hover:bg-[#FFC600] hover:text-black transition-colors"
              >
                <span>Open the Real Estate path</span>
                <span aria-hidden>→</span>
              </Link>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-between bg-transparent text-black font-semibold px-6 py-4 border-2 border-black/30 hover:border-black transition-colors"
              >
                <span>Start a Project</span>
                <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        </section>
      ) : null}

      <section className="bg-black">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((i) => {
            const combo = INDUSTRY_COMBO_LINKS[i.slug];
            return (
              <div
                key={i.slug}
                className="group flex flex-col p-6 lg:p-8 border border-white/15 hover:k-hard-shadow-yellow transition-shadow"
              >
                <Link
                  to={`/industries/${i.slug}`}
                  data-testid={`industries-card-${i.slug}`}
                  className="block"
                >
                  <Overline className="mb-3">Industry</Overline>
                  <h2 className="text-2xl lg:text-3xl font-black tracking-tighter">{i.name}</h2>
                  <p className="mt-3 text-white/70 text-sm">{i.summary}</p>
                  <div className="mt-5 text-sm font-semibold group-hover:text-[#FFC600]">Open →</div>
                </Link>
                {combo ? (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Link
                      to={combo.to}
                      data-testid={`industries-card-${i.slug}-combo`}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[#FFC600] hover:text-white"
                    >
                      <span>{combo.label}</span>
                      <span aria-hidden>→</span>
                    </Link>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
      <CTABlock variant="default" />
    </main>
  );
};

// ---------------------------------------------------------------------------
// Industry detail (full structured page)
// ---------------------------------------------------------------------------
const Bullets = ({ items, testId, dark = false }) => (
  <ul data-testid={testId} className="space-y-2">
    {items.map((it, idx) => (
      <li key={`${idx}-${it}`} className="flex items-start gap-3">
        <span className={`mt-2 inline-block w-2 h-2 ${dark ? "bg-[#FFC600]" : "bg-black"}`} />
        <span className="text-base leading-snug">{it}</span>
      </li>
    ))}
  </ul>
);

export const IndustryDetail = () => {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);
  useSeo({
    title: industry ? `${industry.name} | Kostaja` : "Industry | Kostaja",
    description: industry?.summary || "",
  });

  if (!industry) {
    return (
      <main className="bg-black">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <h1 className="text-5xl font-black tracking-tighter">Industry not found.</h1>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton to="/industries">All industries</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }

  const services = (industry.recommendedServices || industry.bestServices || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);
  const tierCards = services.filter((s) =>
    ["brand-systems", "website-systems", "brand-and-website-packages"].includes(s.category)
  );
  const portfolioForIndustry = PORTFOLIO.filter(
    (p) => p.industry === industry.slug || (p.relatedIndustries || []).includes(industry.slug)
  );
  const relatedNeeds = (industry.bestNeeds || []).map((n) =>
    NEEDS.find((x) => x.slug === n)
  ).filter(Boolean);

  return (
    <main data-testid="industry-detail" className="bg-black">
      {/* HERO */}
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs
            items={[
              { to: "/", label: "Home" },
              { to: "/industries", label: "Industries" },
              { label: industry.name },
            ]}
          />
          <Overline color="yellow" className="mt-6 mb-3">For {industry.name}</Overline>
          <h1 className="text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92] max-w-5xl">
            {industry.heroHeadline}
          </h1>
          <p className="mt-6 max-w-2xl text-white/80 text-base lg:text-lg">{industry.heroSub}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <PrimaryButton href={SITE.bookingUrl} target="_blank" testId="industry-hero-book">
              Start a Project
            </PrimaryButton>
            <SecondaryButton dark to={`/services`} testId="industry-hero-services">
              Explore {industry.name} Services
            </SecondaryButton>
          </div>
        </div>
      </section>

      {/* COMMON PROBLEMS + BUYER CARES */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="p-6 lg:p-8 border border-white/15">
            <Overline className="mb-3">Common problems</Overline>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tighter mb-5 leading-tight">What we fix.</h2>
            <Bullets items={industry.commonProblems} testId="industry-problems" />
          </div>
          <div className="p-6 lg:p-8 border border-white/15 bg-black text-white">
            <Overline color="yellow" className="mb-3">What buyers care about</Overline>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tighter mb-5 leading-tight">Before they trust you.</h2>
            <Bullets items={industry.buyerCares} testId="industry-buyer-cares" dark />
          </div>
        </div>
      </section>

      {/* STARTING PATH */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">Suggested journey</Overline>
            <h2 className="text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]">A clear path, top to bottom.</h2>
            <p className="mt-4 text-white/70">Start where the problem actually lives. Skip steps that already work.</p>
          </div>
          <div className="lg:col-span-7">
            <ol data-testid="industry-path" className="border-l border-white/25">
              {industry.startingPath.map((step, idx) => (
                <li key={step} className="pl-6 pb-7 relative">
                  <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 k-bg-yellow border border-white/15" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60">Step 0{idx + 1}</div>
                  <div className="mt-1 text-lg lg:text-xl font-bold leading-snug">{step}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* RECOMMENDED SERVICES */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <Overline className="mb-3">Recommended services</Overline>
          <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
            Outcomes most {industry.name} clients buy.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.slice(0, 6).map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
          {services.length > 6 ? (
            <div className="mt-10">
              <Overline className="mb-3">More services for {industry.name}</Overline>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
                {services.slice(6).map((s) => (
                  <li key={s.slug} className="bg-black">
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`industry-more-${s.slug}`}
                      className="block p-5 hover:bg-[#FFC600] transition-colors"
                    >
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60">Service</div>
                      <div className="mt-1 text-lg font-black tracking-tighter">{s.name}</div>
                      <div className="mt-2 text-xs text-white/70 line-clamp-2">{s.outcome}</div>
                      <div className="mt-3 text-sm font-semibold">{resolvePricing(s).displayLabel}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      {/* TIER PRICING CARDS (only if 2+ tier-style services) */}
      {tierCards.length >= 2 ? (
        <section className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
            <Overline className="mb-3">Pricing and packages</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95] mb-10">
              Pick the tier that fits the moment.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
              {tierCards.slice(0, 3).map((s, idx) => (
                <div key={s.slug} className={`p-6 lg:p-8 ${idx === 1 ? "bg-[#FFC600] text-black" : "bg-black text-white"}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70">Tier 0{idx + 1}</div>
                  <h3 className="mt-2 text-2xl lg:text-3xl font-black tracking-tighter leading-tight">{s.name}</h3>
                  <div className="mt-3 text-3xl font-black">{resolvePricing(s).displayLabel}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] opacity-70">
                    {billingLabel(s.billing, "long")}
                  </div>
                  <p className="mt-4 text-sm leading-snug opacity-85"><span className="font-semibold">Best for:</span> {s.bestFor}</p>
                  <p className="mt-3 text-sm leading-snug"><span className="font-semibold">Outcome:</span> {s.outcome}</p>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {s.whatYouReceive.slice(0, 5).map((it) => (
                      <li key={`${s.slug}-${it}`} className="flex items-start gap-2">
                        <span className={`mt-1.5 inline-block w-1.5 h-1.5 ${idx === 1 ? "bg-black" : "bg-[#FFC600]"}`} />
                        <span className="opacity-90">{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 text-xs opacity-70"><span className="font-semibold">Not included:</span> {s.notIncluded.slice(0, 2).join(", ")}.</div>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`industry-tier-${s.slug}`}
                    className={`mt-6 inline-flex items-center justify-center font-semibold px-5 py-3 text-sm border-2 ${
                      idx === 1
                        ? "bg-black text-[#FFC600] border-black hover:bg-[#FFC600] hover:text-black"
                        : "bg-[#FFC600] text-black border-[#FFC600] hover:bg-black hover:text-[#FFC600]"
                    } transition-colors`}
                  >
                    View {s.name}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* WHAT HAPPENS AFTER YOU CLICK */}
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline color="yellow" className="mb-3">No mystery process</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">What happens when you click.</h2>
            <p className="mt-4 text-white/70">Every button on the Kostaja site has a known next step. Here is what each one does.</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              { k: "Start a Project", v: "Opens Calendly. You receive a confirmation. Send a short context note before the call so the session opens on the real question." },
              { k: "Purchase a fixed-scope service", v: "Stripe payment. You receive a short intake form. Kostaja confirms scope and begins per the listed timeline." },
              { k: "Purchase an audit", v: "You submit website, brand files, and context. Kostaja sends a written summary and priority recommendations within the listed timeline." },
              { k: "Subscribe monthly", v: "Kostaja confirms scope and cadence. Monthly priorities and deliverables are agreed before each cycle." },
              { k: "Request a quote on a project", v: "A short conversation, then a written scope with timeline, deliverables, and payment structure. A deposit may reserve a start date." },
            ].map((it) => (
              <div key={it.k} className="bg-black p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFC600]">{it.k}</div>
                <p className="mt-2 text-white/85 text-sm leading-snug">{it.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO FOR INDUSTRY */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <Overline className="mb-3">Selected {industry.name} work</Overline>
          {portfolioForIndustry.length ? (
            <>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95] mb-8">Selected work in {industry.name}.</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {portfolioForIndustry.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/portfolio/${p.slug}`}
                    data-testid={`industry-portfolio-${p.slug}`}
                    className="group block border border-white/15 hover:border-[#FFC600] transition-colors overflow-hidden"
                  >
                    <div className="aspect-[16/10] bg-black overflow-hidden">
                      <PortfolioCover item={p} accent="yellow" />
                    </div>
                    <div className="p-6 bg-black">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFC600] font-bold">{p.type}</span>
                        <span className="text-white/30">·</span>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">{p.category}</span>
                      </div>
                      <h3 className="text-2xl font-black mt-2 tracking-tighter leading-tight text-white group-hover:text-[#FFC600] transition-colors">{p.title}</h3>
                      <p className="text-sm text-white/70 mt-2">{p.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="industry-portfolio-empty" className="border border-white/15 p-8 lg:p-10">
              <h3 className="text-2xl lg:text-4xl font-black tracking-tighter leading-tight max-w-2xl">
                Work in this industry that is ready to show will appear here.
              </h3>
              <p className="mt-3 text-white/70 max-w-2xl">In the meantime, the strongest way to see if Kostaja fits is a 30 minute working call.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <PrimaryButton href={SITE.bookingUrl} target="_blank">Start a Project</PrimaryButton>
                <SecondaryButton to="/portfolio">View full portfolio</SecondaryButton>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RELATED NEEDS */}
      {relatedNeeds.length ? (
        <section className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16">
            <Overline className="mb-3">Related needs</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95] mb-8">Or browse by the problem.</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
              {relatedNeeds.map((n) => (
                <li key={n.slug} className="bg-black">
                  <Link
                    to={`/needs/${n.slug}`}
                    data-testid={`industry-need-${n.slug}`}
                    className="block p-5 hover:bg-[#FFC600] transition-colors"
                  >
                    <Overline className="mb-2">Need</Overline>
                    <div className="text-lg font-black tracking-tighter">{n.name}</div>
                    <p className="mt-2 text-sm text-white/70 line-clamp-2">{n.summary}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <RelatedContent
        serviceSlugs={(industry.recommendedServices || industry.bestServices || []).slice(0, 6)}
        needSlugs={industry.bestNeeds || []}
        heading={`More for ${industry.name}`}
      />

      {/* LEARN MORE - INDUSTRY */}
      <section className="bg-black border-b border-white/10" data-testid="industry-learn-more">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
            <div>
              <Overline className="mb-3">Learn more before you decide</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95]">
                Useful reading for {industry.name} owners.
              </h2>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
            {learnMoreForIndustry(industry.slug).map((l) => (
              <LearnMoreCard key={l.slug} item={l} testId={`industry-learn-${l.slug}`} />
            ))}
          </ul>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/15">
            <div className="bg-black p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">Contact</div>
              <p className="mt-2 text-sm text-white/85 leading-snug">{CTA_CONTEXT.contact}</p>
            </div>
            <div className="bg-black p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">Strategy Call</div>
              <p className="mt-2 text-sm text-white/85 leading-snug">{CTA_CONTEXT.book}</p>
            </div>
            <div className="bg-black p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold k-yellow">Project Quote</div>
              <p className="mt-2 text-sm text-white/85 leading-snug">{CTA_CONTEXT.quote}</p>
            </div>
          </div>
        </div>
      </section>

      <CTABlock variant="industry" />
    </main>
  );
};